import { switchMap, tap } from 'rxjs/operators';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { LocalDate } from '../../../core/model/local-date';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { LeaseAgreementService } from '../../service/data/lease-agreement.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Invoice } from '../../../invoice/model/invoice';
import { ListViewRow } from '../../../core/model/list-view-row';
import { LeasePeriodKind } from '../../model/const/enums';
import { NativeWindowService } from '../../../core/service/native-window.service';
import { MessageService } from '../../../core/service/message.service';
import {
  ESignatory,
  InformationChannelKind,
  SignatureReminderSettingKind,
  SignatureTypeKind,
  SigningRoleKind,
} from '../../model/esignatory';
import { SelectionItem } from '../../../core/component/select/select.component';
import { configureESignatoryContactValidation } from '../../helper/esignatory-form-validation';

@Component({
    selector: 'app-terminate-lease-agreement',
    templateUrl: './terminate-lease-agreement.component.html',
    styleUrls: ['./terminate-lease-agreement.component.scss'],
    providers: [NavigationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TerminateLeaseAgreementComponent implements OnDestroy, OnInit {
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _fcf: FormControlExFactory,
    private _navigation: NavigationService,
    private _leaseAgreementService: LeaseAgreementService,
    private _fb: FormBuilder,
    private _cf: ColumnDefinitionFactory,
    private _nativeWindow: NativeWindowService,
    private _messageService: MessageService
  ) {
    this._subscriptions = new Subscription();

    this.terminationDate = LocalDate.today();
    this.lastDebitDate = new LocalDate(null, null, null);
    this.desiredRemovalDate = new LocalDate(null, null, null);
    this.validContractPeriodToLandlord = new LocalDate(null, null, null);
    this.validContractPeriodToTenant = new LocalDate(null, null, null);

    this._terminatedBy = 0;

    this.form = this._fb.group({
      invoices: this._fb.control([]),
      invoiceIds: this._fb.control([]),
      terminationDate: this._fb.group({}),
      lastDebitDate: this._fb.group({}),
      desiredRemovalDate: this._fb.group({}),
      terminatedBy: this._fcf.createMandatory('TERMINATE_LEASE_AGREEMENT_FORM.TERMINATED_BY_LABEL', this._terminatedBy),
      useDigitalSigning: this._fcf.create('TERMINATE_LEASE_AGREEMENT_FORM.USE_DIGITAL_SIGNING', false),
      requireSignatureBeforeTermination: this._fcf.create('TERMINATE_LEASE_AGREEMENT_FORM.REQUIRE_SIGNATURE_BEFORE_TERMINATION', false),
      landlordMessage: this._fcf.createMaxLength('TERMINATE_LEASE_AGREEMENT_FORM.LANDLORD_MESSAGE', '', 4000),
      expirationDate: this._fb.group({}),
      errandDescription: this._fcf.createMandatory('ESIGNATURE.DESCRIPTION', ''),
      informWhenSigned: this._fcf.create('ESIGNATURE.INFORM_WHEN_SIGNED', true),
      informEmailAddress: this._fcf.create('ESIGNATURE.INFORM_EMAIL_ADRESSES', ''),
      signatories: this._fb.array([]),
    });

    this.invoicesColumnDefinitions = [
      this._cf.create('INVOICE.INVOICE_NUMBER', 'invoiceNumber'),
      this._cf.create('INVOICE.RECIPIENT_NAME', 'invoiceRecipient'),
      this._cf.create('INVOICE.PERIOD', 'refersToPeriod'),
      this._cf.createDate('DUE_PERIODS.INVOICE_PERIOD_FROM', 'periodFrom', ColumnVisibility.optional),
      this._cf.createDate('DUE_PERIODS.INVOICE_PERIOD_TO', 'periodTo', ColumnVisibility.optional),
      this._cf.createDate('INVOICE.DUE_DATE', 'dueDate'),
      this._cf.createMoney('INVOICE.REMAINING_AMOUNT', 'remainingAmount'),
      this._cf.createMoney('INVOICE.INVOICEAMOUNT', 'totalAmount'),
    ];

    this._setDates(this._terminatedBy, this.terminationDate);
  }

  invoicesColumnDefinitions: ColumnDefinition[];
  invoices: ListViewRow[];
  allInvoices: ListViewRow[];
  form: FormGroup;
  terminationDate: LocalDate;
  private _terminatedBy: number;
  lastDebitDate: LocalDate;
  desiredRemovalDate: LocalDate;
  leaseAgreementId: string;
  private _noticePeriodLandlord: number;
  private _noticePeriodTenant: number;
  leaseAgreementNumber: string;
  useDwellingInspection: boolean;
  hasJointDebitInvoice: boolean;
  dwellingInspectionTypeMainName: string;
  dwellingInspectionTypeOptionalName: string;
  leasePeriodType: LeasePeriodKind = LeasePeriodKind.UntilFurtherNotice;
  validContractPeriodToLandlord: LocalDate;
  validContractPeriodToTenant: LocalDate;
  expirationDate: LocalDate;
  landlordTerminationMessage: string;
  private _useDigitalSigningWhenTerminatedByTenant = false;
  private _useDigitalSigningWhenTerminatedByLandlord = false;
  private _requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning = false;
  private _eSignatureSignatories: ESignatory[] = [];
  private _hasActiveTerminationSignatureErrand = false;
  private _cancelExistingTerminationSignatureErrand = false;

  terminatedByItems = [
    { name: 'TERMINATE_LEASE_AGREEMENT_FORM.TENANT_TEXT', value: 0 },
    { name: 'TERMINATE_LEASE_AGREEMENT_FORM.LANDLORD_TEXT', value: 1 },
  ];

  signatureTypeSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.BANKID', SignatureTypeKind.BankId),
    new SelectionItem('ESIGNATURE.SIGNATURE_TYPE.CLICK', SignatureTypeKind.Click),
  ];

  informationChannels = [
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL', InformationChannelKind.Email),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.SMS', InformationChannelKind.Sms),
    new SelectionItem('ESIGNATURE.INFORMATION_CHANNEL.EMAIL_AND_SMS', InformationChannelKind.EmailAndSms),
  ];

  signingRoles = [
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.COMPANY_REPRESENTATIVE', SigningRoleKind.CompanyRepresentative),
    new SelectionItem('ESIGNATURE.SIGNING_ROLE.TENANT', SigningRoleKind.Tenant),
    new SelectionItem('LEASE_AGREEMENT.CREDITOR', SigningRoleKind.Guarantor),
  ];

  signatureReminderSettingSelection = [
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.NO_REMINDERS', SignatureReminderSettingKind.NoReminders),
    new SelectionItem('ESIGNATURE.SIGNATURE_REMINDER_SETTING.ONCE_A_DAY', SignatureReminderSettingKind.OnceADay),
  ];

  get displayCreditInvoices(): boolean {    
    return this.invoices !== undefined && this.invoices.length > 0;
  }

  get displayDigitalSigning(): boolean {
    return !!this.form.controls['useDigitalSigning'].value;
  }

  get terminatedByLandlord(): boolean {
    return this._terminatedBy === 1;
  }

  get signatoryForms(): FormGroup[] {
    return (this.form.controls['signatories'] as FormArray).controls.map(control => control as FormGroup);
  }

  ngOnInit() {
    this.AddSubscription(
      this._activatedRoute.paramMap
        .pipe(
          switchMap(map => {
            let id = map.get('leaseAgreementId');
            this.leaseAgreementId = id;
            return this._leaseAgreementService.getDataForTermination(id);
          }),
        )
        .subscribe(data => {
          this._setLeaseAgreementData(data);
          this.AddSubscription(this.form.controls['terminatedBy'].valueChanges.subscribe(value => this._onTerminatedByChanged(value)));
          this.AddSubscription(this.form.controls['lastDebitDate'].valueChanges.subscribe(value => this._onLastDebitDateChanged(value)));
          this.AddSubscription(this.form.controls['terminationDate'].valueChanges.subscribe(value => this._onTerminationDateChanged(value)));
          this.AddSubscription(this.form.controls['useDigitalSigning'].valueChanges.subscribe(value => this._onDigitalSigningChanged(value)));
        })
    );
  }

  private _getNoticePeriod(terminatedBy: number): number {
    return terminatedBy === 1 ? this._noticePeriodLandlord : this._noticePeriodTenant;
  }

  private _onTerminatedByChanged(newValue: number) {
    this._terminatedBy = newValue;
    this._setDates(this._terminatedBy, this.terminationDate);
    this._setDigitalSigningDefaults();
  }

  private _onDigitalSigningChanged(useDigitalSigning: boolean): void {
    const eSignatureControls = [
      'requireSignatureBeforeTermination',
      'landlordMessage',
      'expirationDate',
      'errandDescription',
      'informWhenSigned',
      'informEmailAddress',
    ];

    for (const controlName of eSignatureControls) {
      const control = this.form.controls[controlName];
      if (useDigitalSigning) control.enable({ emitEvent: false });
      else control.disable({ emitEvent: false });
    }

    const signatories = this.form.controls['signatories'] as FormArray;
    if (useDigitalSigning) signatories.enable({ emitEvent: false });
    else signatories.disable({ emitEvent: false });

    if (useDigitalSigning && this.signatoryForms.length === 0) {
      for (const signatory of this._eSignatureSignatories) {
        this.addSignatory(signatory);
      }
    }
  }

  private _onTerminationDateChanged(newValue: LocalDate) {
    this._setDates(this._terminatedBy, newValue);
  }

  private _onLastDebitDateChanged(newValue: LocalDate) {
    this.invoices = this.allInvoices.filter(x => x.dataSource.periodTo.toDate() > LocalDate.fromDate(newValue).toDate());
    this.form.controls['invoices'].setValue(this.invoices);
    this._setLocalDateFormValue('desiredRemovalDate', newValue);
  }

  private _setLocalDateFormValue(formName: string, newValue: LocalDate, form?: FormGroup) {
    var usedForm = form ? form : this.form;
    let dateForm = usedForm.controls[formName] as FormGroup;
    if (dateForm && dateForm.contains('year') && dateForm.contains('month') && dateForm.contains('day')) {
      dateForm.setValue({ year: newValue.year, month: newValue.month, day: newValue.day });
      dateForm.markAsDirty();
    }
  }

  private _setDates(terminatedBy: number, terminationDate: LocalDate) {
    let noticePeriod = this._getNoticePeriod(terminatedBy);
    let lastDebit = LocalDate.fromDate(terminationDate).clone();
    if (this.leasePeriodType != LeasePeriodKind.AutomaticExtension) {
      lastDebit.addMonths(noticePeriod);
      lastDebit.setDate(lastDebit.lastDayInMonth());
    } else {
      if (this.validContractPeriodToTenant) lastDebit.setDate(this.validContractPeriodToTenant.toDate());
      else {
        lastDebit.addMonths(noticePeriod);
        lastDebit.setDate(lastDebit.lastDayInMonth());
      }
    }
    if (lastDebit.toDate() != this.lastDebitDate.toDate()) {
      this.lastDebitDate.setDate(lastDebit.toDate());
      this.desiredRemovalDate.setDate(lastDebit.toDate());
      this._setLocalDateFormValue('lastDebitDate', lastDebit);
      this._setLocalDateFormValue('desiredRemovalDate', lastDebit);
    }
  }

  private _setLeaseAgreementData(context: any) {
    if (context) {
      this._noticePeriodLandlord = context.landlordNoticePeriod;
      this._noticePeriodTenant = context.tenantNoticePeriod;
      this.leaseAgreementNumber = context.leaseAgreementNumber;
      this.useDwellingInspection = context.useDwellingInspection;      
      this.hasJointDebitInvoice = context.hasJointDebitInvoice;
      this.dwellingInspectionTypeMainName = context.dwellingInspectionTypeMainName;
      this.dwellingInspectionTypeOptionalName = context.dwellingInspectionTypeOptionalName;
      this.leasePeriodType = context.leasePeriodType;
      this.validContractPeriodToLandlord = LocalDate.fromDate(context.validContractPeriodToLandlord);
      this.validContractPeriodToTenant = LocalDate.fromDate(context.validContractPeriodToTenant);
      this._useDigitalSigningWhenTerminatedByTenant = !!context.useDigitalSigningWhenTerminatedByTenant;
      this._useDigitalSigningWhenTerminatedByLandlord = !!context.useDigitalSigningWhenTerminatedByLandlord;
      this._requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning =
        !!context.requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning;
      this.landlordTerminationMessage = context.landlordTerminationMessage || '';
      this.expirationDate = LocalDate.fromDate(LocalDate.today().addDays(context.eSignatureDefaultResponseTime || 7));
      // The date picker is only rendered after digital signing is selected. It registers
      // year/month/day controls in this group during its own initialization, so avoid
      // setting a complete value while the group is still empty.
      this._setLocalDateFormValue('expirationDate', this.expirationDate);
      this.form.controls['errandDescription'].setValue(context.eSignatureErrandDescription || 'Uppsägning av hyresavtal');
      this.form.controls['informEmailAddress'].setValue(context.eSignatureInformEmailAddress || '');
      this._eSignatureSignatories = (context.eSignatureSignatories || []).map(item => Object.assign(new ESignatory(), item));
      this._hasActiveTerminationSignatureErrand = !!context.hasActiveTerminationSignatureErrand;
      this._setDigitalSigningDefaults();
      this._setDates(this._noticePeriodTenant, LocalDate.today());
      var invoi = context.invoices.map(item => Object.assign(new Invoice(), item));
      this.allInvoices = invoi.map(inv => new ListViewRow(inv));
      this.invoices = this.allInvoices.filter(x => x.dataSource.periodTo.toDate() > LocalDate.fromDate(this.lastDebitDate).toDate());
      this.form.controls['invoices'].setValue(this.invoices);
    }
  }

  private _setDigitalSigningDefaults(): void {
    const useDigitalSigning = this.terminatedByLandlord
      ? this._useDigitalSigningWhenTerminatedByLandlord
      : this._useDigitalSigningWhenTerminatedByTenant;
    this.form.controls['useDigitalSigning'].setValue(useDigitalSigning, { emitEvent: false });
    this.form.controls['requireSignatureBeforeTermination'].setValue(
      this.terminatedByLandlord && this._requireSignatureBeforeTerminationWhenLandlordUsesDigitalSigning,
      { emitEvent: false }
    );
    this.form.controls['landlordMessage'].setValue(this.landlordTerminationMessage, { emitEvent: false });
    this._onDigitalSigningChanged(useDigitalSigning);
  }

  private addSignatory(signatory: ESignatory): void {
    const signatoryForm = this._fb.group({
        civicNumber: this._fcf.create('ESIGNATURE.SIGNATORY.CIVIC_NUMBER', signatory.civicNumber),
        email: this._fcf.createEmail('ESIGNATURE.SIGNATORY.EMAIL', signatory.email),
        informationChannel: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.INFORMATION_CHANNEL', signatory.informationChannel),
        signatureType: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_TYPE', signatory.signatureType),
        name: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.NAME', signatory.name),
        phone: this._fcf.create('ESIGNATURE.SIGNATORY.PHONE', signatory.phone),
        signOrder: this._fcf.create('ESIGNATURE.SIGNATORY.SIGN_ORDER', signatory.signOrder),
        signingRole: this._fcf.createMandatory('COMMON.ROLE', signatory.signingRole),
        signatureReminderSetting: this._fcf.createMandatory('ESIGNATURE.SIGNATORY.SIGNATURE_REMINDER_SETTING', signatory.signatureReminderSetting),
      });
    (this.form.controls['signatories'] as FormArray).push(signatoryForm);
    this.AddSubscription(configureESignatoryContactValidation(signatoryForm));
  }

  newSignatory(): void {
    this.addSignatory(new ESignatory());
  }

  deleteSignatory(index: number): void {
    (this.form.controls['signatories'] as FormArray).removeAt(index);
  }

  private readonly _subscriptions: Subscription;
  private _resultSub: Subscription;

  protected AddSubscription(subscription: Subscription): void {
    if (!subscription) {
      throw new Error('Null reference. Object not set.');
    }

    this._subscriptions.add(subscription);
  }

  private clearSubscriptions() {
    if (this._resultSub && !this._resultSub.closed) this._resultSub.unsubscribe();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    this.clearSubscriptions();
  }

  cancel() {
    this._navigation.navigateBack();
  }

  previewDigitalSigningDocument(): void {
    if (!this.displayDigitalSigning) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const context = this.createTerminationContext();
    this.AddSubscription(
      this._leaseAgreementService.previewTerminationForESign(context).subscribe(document => {
        this._nativeWindow.openFile(document, `${this.leaseAgreementNumber}-uppsagning.pdf`);
      })
    );
  }

  save() {
    if (this.form.disabled) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this._hasActiveTerminationSignatureErrand
      && !this._cancelExistingTerminationSignatureErrand) {
      this.AddSubscription(
        this._confirmCancellationOfExistingTerminationSignatureErrand().subscribe(confirmed => {
          if (confirmed) this.save();
        })
      );
      return;
    }

    const context = this.createTerminationContext();
    this.form.disable();
    const action = this.displayDigitalSigning
      ? this._leaseAgreementService.terminateForESign(context)
      : this._leaseAgreementService.terminate(context);

    this.AddSubscription(
      action.subscribe(
        () => this._navigation.navigateBack(),
        error => {
          this.form.enable();
        }
      )
    );
  }

  private createTerminationContext(): any {
    this.form.controls['invoiceIds'].setValue(this.invoices.filter(x => x.selected).map(x => x.dataSource.id));
    const context = this.form.value;
    context.id = this.leaseAgreementId;
    context.cancelExistingTerminationSignatureErrand = this._cancelExistingTerminationSignatureErrand;
    return context;
  }

  private _confirmCancellationOfExistingTerminationSignatureErrand() {
    return this._messageService
      .showDialog('TERMINATE_LEASE_AGREEMENT_FORM.ACTIVE_ESIGNATURE_CONFIRMATION')
      .pipe(tap(confirmed => this._cancelExistingTerminationSignatureErrand = confirmed));
  }
}
